import { useTranslation } from 'react-i18next';
import {
  TripDayInsertBetween,
  TripExperienceFrame,
  TripExperienceFrameHeader,
  useDays,
  useTripStore,
} from '@koob/trip-designer';
import { formatCurrency } from '../../../../utils';

// §3 — a single-day frame names its city, a longer one names both ends, and
// both ends are always filled in.
const getItineraryLabel = (frame, t) =>
  frame.startLocation &&
  frame.endLocation &&
  frame.startLocation !== frame.endLocation
    ? t('compose.frame.itinerary', {
        from: frame.startLocation,
        to: frame.endLocation,
      })
    : (frame.startLocation ?? frame.endLocation ?? null);

// One experience of the timeline: the separator that opens it, then its header
// and its day cards inside one frame.
export default function ComposeMapFrame({
  frame,
  dayCards,
  dayRangeLabel,
  registry,
}) {
  const { t } = useTranslation('tripDesigner');
  const days = useDays();
  const experiencesMap = useTripStore(state => state.experiences);
  const { getGlobalServicesSlotRef, removableFrames, removeFrameExperience } =
    registry;
  const firstDayIndex = frame.dayIndices[0];

  return (
    <div className="mb-3">
      {/* The separator that opens the block belongs to the
      timeline, between the previous experience and this one
      — drawn from day 1 it was boxed inside the frame,
      between the header and the first day card. The shape
      mirrors what `useExperienceField` hands the day: this
      control reads `id` and `dayIndex` off it, the latter
      telling two consecutive instances of the same
      experience apart. */}
      <TripDayInsertBetween
        dayIndex={firstDayIndex}
        position="before"
        experience={{
          ...experiencesMap?.[frame.experienceId],
          dayIndex: days?.[firstDayIndex]?.experienceDayIndex,
        }}
        translation={t}
      />
      <TripExperienceFrame
        globalServices={
          <div ref={getGlobalServicesSlotRef(frame.startDayIndex)} />
        }
        header={
          <TripExperienceFrameHeader
            name={frame.name}
            factsheetUrl={frame.factsheetUrl}
            factsheetLabel={t('compose.frame.openFactsheet')}
            imageUrl={frame.imageUrl ?? '/img/hotel-image-placeholder.png'}
            dayRangeLabel={dayRangeLabel}
            itineraryLabel={getItineraryLabel(frame, t)}
            // §9 — the experience price is shown here and
            // nowhere else; the days it covers never
            // repeat it.
            priceLabel={
              frame.price !== null
                ? formatCurrency({
                    amount: frame.price,
                    currency: frame.currency,
                  })
                : null
            }
            pricePerPersonLabel={
              frame.pricePerPerson !== null
                ? t('compose.frame.pricePerPerson', {
                    price: formatCurrency({
                      amount: frame.pricePerPerson,
                      currency: frame.currency,
                    }),
                  })
                : null
            }
            // §3 — one cross per experience, in its header.
            // It calls back into the first day, which owns
            // the removal and its required-options warning.
            onRemove={
              removableFrames[frame.startDayIndex]
                ? () => removeFrameExperience(frame.startDayIndex)
                : undefined
            }
            removeLabel={t('compose.frame.removeExperience')}
          />
        }
      >
        {dayCards}
      </TripExperienceFrame>
    </div>
  );
}
